import {useTheme} from "styled-components";
import {useLocale, useMessages} from "i18n";
import {useHasMounted} from "utils/useHasMounted";
import {useMountedHostname} from "utils/useMountedHostname";
import {ADVERTISE_PATH} from "sponsor/sponsorRoutes";
import type {BackdropTemplateKey} from "sponsor/sponsorBooking";

import {previewBackdropArt} from "sponsor/takeover/backdropArt";
import {SponsorTakeoverArt} from "sponsor/takeover/SponsorTakeover";
import {ADVERTISING_HERO_ART_TESTID} from "sponsor/takeover/takeoverTestIds";
import {AdLabel} from "sponsor/takeover/SponsorTakeover.styles";
import {placeholderArtUrls} from "sponsor/BackdropTemplatePicker.styles";
import {ADVERTISED_TREATMENTS} from "./advertisedTreatments";
import {splitAccentedHeadline} from "./accentedHeadline";
import {advertisingCopy, type AdvertisingMockCopy} from "./copy/advertising";
import {CtaRow, SecondaryLink, StartAdvertisingLink} from "./AdvertisingContent.styles";
import {
	AccentWord,
	BrowserFrame,
	DemoCaption,
	FrameBar,
	FrameDot,
	FrameStage,
	FrameUrl,
	HeroBand,
	HeroDisplay,
	HeroEyebrow,
	HeroGrid,
	HeroLede,
	MockAction,
	MockAdCard,
	MockAdCta,
	MockAdHeadline,
	MockAdSponsor,
	MockAdTagline,
	MockCard,
	MockCount,
	MockFileKind,
	MockFileName,
	MockFileRow,
	MockFileSize,
	MockFoot,
	MockSender,
	PencilStroke,
	PencilStrokeMain,
	PencilStrokeTail,
} from "./AdvertisingHero.styles";

/**
 * The `/advertising` landing hero (ui#1156, reworked in ui#1861): the pitch, beside a picture of the
 * page it is selling, with a REAL sponsor takeover running inside that picture.
 *
 * The backdrop is not a screenshot, a video or a mock. It is {@link SponsorTakeoverArt} — the exact
 * component a recipient's download page mounts — fed the token-derived stand-in plates the booking
 * page's own picker uses. That is the whole argument for the section: a marketing mock-up of an
 * animation is a promise the product has to keep later, and this one cannot drift from what is sold
 * because it IS what is sold.
 *
 * ## What is real in the frame, and what is a stand-in
 *
 * Real: the renderer, and the ad's DISCLOSURE — {@link AdLabel} fed `sponsorTakeover.label`, the same
 * component and the same catalog string a recipient's chip carries. That is the part the 2026-08-06
 * review is about, so it is the part that must not be a marketing rendition of itself.
 *
 * Stand-in: the download card, the file rows, the sender's name, and the ad card AROUND that label.
 * Every one is a token-only presentational element with nothing focusable in it. The full
 * `SponsorTakeoverChip` is deliberately not mounted, for two independent reasons: it renders a real
 * outbound anchor with `rel="nofollow sponsored"`, and a dead link inside a picture of a page is worse
 * than no link; and it claims the `AD_CHIP_TESTID` the recipient-page specs prove
 * ABSENCE through, which a marketing demo answering to would quietly make vacuous — the same trap
 * `takeoverTestIds.ts` records for the art layer, and why this mount passes its own id. Its LAYOUT does
 * not fit here either; `MockAdCard` in the styles file has that measurement.
 *
 * The whole frame is one `role="img"` with a name that says what it is, and its interior is hidden from
 * assistive technology. A screen reader announcing "Ana sent you, 2 files, Download all" would be
 * describing a transfer that does not exist.
 *
 * ## Two different costs, and only one of them was worth paying
 *
 * The MODULE cost is nil, and that is why the imports above are static. `/advertising` is eager in
 * `routes.tsx`, but so is `/advertise`, whose `SponsorPage` already pulls `BackdropTemplatePicker` →
 * `SponsorTakeoverArt` → `SponsorEffects` → framer-motion into the same `app.js`.
 *
 * The PRERENDER cost was real, and is what {@link useHasMounted} is doing below. Rendered statically,
 * the frame serialises a background `data:` URI onto every one of its tiles: with the art mounted during
 * the pre-render, `build/advertising/index.html` measured 36,030 → 82,052 bytes, 23,508 of them the same
 * six SVG plates written out 24 times. Mounting the art AFTER hydration keeps the page's HTML free of
 * `data:` URIs entirely, and costs only that the backdrop arrives a frame late on the field colour it
 * fades in over. The demo caption is gated on the same flag, so the static HTML never describes art it
 * does not contain.
 *
 * `useHasMounted` specifically, and not a bare `useEffect` flag: it returns `false` for the static
 * render AND for the first client render, so the tree React hydrates is byte-identical to the HTML it
 * hydrates against (ui#74). Gating on anything that differs between those two renders is the hydration
 * mismatch this repo has already paid for once.
 *
 * ## Reduced motion, and the static frame
 *
 * Handled INSIDE the takeover, not here: `useTakeoverPlayback` already resolves reduced motion, an
 * offscreen layer and a backgrounded tab to one `still` flag, and each effect answers it with a
 * different COMPOSITION rather than a paused animation. Re-deciding it at this call site would be a
 * second, drifting answer to a question the renderer already owns.
 */
export function AdvertisingHero() {
	const {locale} = useLocale();
	const {hero, cta, heading} = advertisingCopy[locale];
	const treatmentName = useMessages().sponsor.templates[HERO_TREATMENT].name;
	const hasMounted = useHasMounted();
	const headline = splitAccentedHeadline(hero.headline, hero.accentWord);

	return (
		<HeroBand>
			<HeroGrid>
				<div>
					{/* The page's subject line, which the `<h1>` no longer carries — see the copy module. */}
					<HeroEyebrow>{heading}</HeroEyebrow>
					{/*
					  * Named explicitly, because splitting the sentence changes what a screen reader hears.
					  * Wrapping the accent word puts an element boundary inside the heading, and the accessible
					  * name algorithm inserts whitespace at one: the Romanian heading computed as
					  * "Fii văzut exact când toți așteaptă ." — a space before the full stop. English hid it,
					  * because its remainder (" for.") already begins with a space that the extra one collapses
					  * into, so the defect was invisible in the only locale anybody looks at first.
					  *
					  * The label is the SAME catalog string the runs below are sliced from, so it cannot drift
					  * from the visible text and WCAG 2.5.3's label-in-name holds by construction.
					  */}
					<HeroDisplay aria-label={hero.headline}>
						{headline.lead}
						{headline.accent !== "" && (
							<AccentWord>
								{headline.accent}
								<PencilUnderline/>
							</AccentWord>
						)}
						{headline.trail}
					</HeroDisplay>
					<HeroLede>{hero.lede}</HeroLede>
					<CtaRow>
						<StartAdvertisingLink to={ADVERTISE_PATH}>{cta.label}</StartAdvertisingLink>
						{/* The same destination as the button. It names the thing this page deliberately does
						    not answer — the rate — and sends the reader to the calendar that qualifies it. */}
						<SecondaryLink to={ADVERTISE_PATH}>{hero.secondary}</SecondaryLink>
					</CtaRow>
				</div>
				<div>
					<DownloadPageMock mock={hero.mock} hasMounted={hasMounted}/>
					{/*
					  * Gated on the SAME condition as the art it describes. Rendered unconditionally, the
					  * pre-rendered HTML — which is what a crawler and a no-JS reader get — announced "a live
					  * example ... running the real renderer" over a still frame.
					  */}
					{/* The count is the catalogue's own length, the same figure the terms strip and the
					    gallery heading state (kitecorp/transferify-ui#1870). Both locales used to write
					    "six" into this sentence, which the wallpapers made wrong the day they shipped. */}
					{hasMounted
						&& <DemoCaption>{hero.demoCaption(treatmentName, ADVERTISED_TREATMENTS.length)}</DemoCaption>}
				</div>
			</HeroGrid>
		</HeroBand>
	);
}

/**
 * The picture of a download page.
 *
 * One `role="img"` with a name, and `aria-hidden` on everything inside it. The alternative — leaving the
 * interior in the accessibility tree — announces a sender, two files and a download button that do not
 * exist, which is a worse outcome than a picture nobody can inspect.
 */
function DownloadPageMock({mock, hasMounted}: {mock: AdvertisingMockCopy; hasMounted: boolean}) {
	const {sponsorTakeover, downloadRich} = useMessages();
	/*
	 * The host the READER is on, never a canonical constant (ui#1517, and `displayed-origin-sweep`
	 * enforces it). It is the right answer twice over: the frame is a picture of a download page, and
	 * the download page this reader's recipients will land on is served from the host in front of them
	 * — a tenant's claimed domain included. `siteOrigin(locale)` would have shown the main brand's address to
	 * somebody reading on `.ro`, which is the exact defect that rule exists for.
	 *
	 * `""` until mounted, which prints the path alone. That is the same hydration gate the art behind
	 * this bar uses, and one pre-rendered tree is served to several hosts, so there is no host the bake
	 * could have written here anyway.
	 */
	const host = useMountedHostname();
	return (
		<BrowserFrame role="img" aria-label={mock.frameAria}>
			<div aria-hidden="true">
				<FrameBar>
					<FrameDot/>
					<FrameDot/>
					<FrameDot/>
					<FrameUrl>{mock.url(host)}</FrameUrl>
				</FrameBar>
				<FrameStage>
					{hasMounted && <HeroTakeover/>}
					{/*
					  * The layout is a stand-in and the DISCLOSURE is not — see {@link MockAdCard} for why the
					  * real chip could not be mounted at this size. `AdLabel` and `sponsorTakeover.label` are
					  * both the recipient page's own, so the "Ad" here is the real one.
					  */}
					<MockAdCard>
						<AdLabel>{sponsorTakeover.label}</AdLabel>
						<MockAdHeadline>
							{mock.ad.headline}{" "}
							<MockAdSponsor>{sponsorTakeover.byLine(mock.ad.sponsor)}</MockAdSponsor>
						</MockAdHeadline>
						<MockAdTagline>{mock.ad.tagline}</MockAdTagline>
						<MockAdCta>{mock.ad.cta}</MockAdCta>
					</MockAdCard>
					<MockCard>
						<MockSender>{mock.sender}</MockSender>
						<MockCount>{mock.fileCount}</MockCount>
						{mock.files.map((file, index) => (
							<MockFileRow key={file.name}>
								{/* The first row is the video, the second the document — the two chip colours the
								    real download page paints, keyed by position rather than by parsing a name. */}
								<MockFileKind $kind={index === 0 ? "video" : "document"}>{file.kind}</MockFileKind>
								<MockFileName>{file.name}</MockFileName>
								<MockFileSize>{file.size}</MockFileSize>
							</MockFileRow>
						))}
						<MockAction>{mock.download}</MockAction>
						{/*
						  * The footer an UNBRANDED download page carries, from the same rich-text entry the real
						  * page renders it from. Included rather than tidied away: the accuracy contract in the
						  * copy module refuses to claim there is nothing else on the screen selling anything,
						  * and a mock that omitted this would be making that claim by picture instead.
						  */}
						<MockFoot>{downloadRich.poweredBy()}</MockFoot>
					</MockCard>
				</FrameStage>
			</div>
		</BrowserFrame>
	);
}

/**
 * The live takeover itself, split out so the static render does no part of this work.
 *
 * Building the stand-in plates is not free — {@link placeholderArtUrls} composes six SVG data URIs on
 * every call — and neither is reading the theme. Kept in the parent behind the `hasMounted` guard, both
 * would still run during the pre-render, for a subtree that is deliberately not emitted.
 */
function HeroTakeover() {
	const {tokens} = useTheme();
	const art = previewBackdropArt(HERO_TREATMENT, [], placeholderArtUrls(tokens));
	/*
	 * `drive="sweep"`, which is the PREVIEW presentation — and here that is the accurate one, not a
	 * shortcut. The default `scroll` is built for a full-height recipient page: it pairs the layer's
	 * 42–55% dimmer with `TakeoverScrim`'s centre wash, an opaque ellipse of paper through the middle of
	 * the band. Inside a 420px frame that ellipse covers most of the art the frame exists to show, and
	 * the scroll drive itself has almost no travel to read against. `sweep` selects `showcaseStrength`
	 * and drops the centre wash — the same two things the big preview overlay asks for.
	 *
	 * The tempo it also selects is a 1.25x speedup (`BAND_PREVIEW_SPEEDUP`), so one traversal goes from
	 * 56s to ~45s. Still ambient at this scale; not a reason to keep the art invisible.
	 */
	return <SponsorTakeoverArt art={art} testId={ADVERTISING_HERO_ART_TESTID} drive="sweep"/>;
}

/**
 * The treatment the hero runs.
 *
 * The band, chosen because this mount is a BACKDROP behind a card rather than a stage: it has an even
 * density across the whole width, its edges are dissolved by a mask instead of ending at a clip
 * rectangle, and it drifts linearly with no cycle, so nothing in it pulses or restarts under the card.
 * The deck, the ring and the coverflow all concentrate mass at one point, which is where the card is.
 *
 * Typed to EXCLUDE the hero treatment, because that one is the single case that cannot be a backdrop:
 * `showsWholeImage` marks `HERO_SCROLL` as FITTED (`sponsorMotion.ts`), so it composes one photograph
 * centred and whole rather than filling the frame. Behind a download card that reads as a picture the
 * card is sitting on, not as a band running behind it — and it is the one treatment whose whole design
 * is a single plate, so there is nothing for the eye to follow. The type turns a plausible-looking
 * one-word edit into a compile error.
 */
const HERO_TREATMENT: Exclude<BackdropTemplateKey, "HERO_SCROLL"> = "SCROLLING_BARS";

/**
 * The hand-drawn stroke under the accent word.
 *
 * Inline SVG rather than a border or a background image, because the line has to be DRAWN: the stroke
 * is dashed to its own length and the dash offset animates to zero, so it appears to be written left to
 * right. `pathLength="1"` normalises that length, which is what lets one dash rule serve two paths of
 * different real lengths and survive any later reshaping of either.
 *
 * `preserveAspectRatio="none"` stretches the box to whatever width the word is; `vector-effect` keeps
 * the stroke an even weight through that stretch, which is the difference between a pencil line and a
 * wedge. `aria-hidden` because it carries no information — the word is already in the heading text.
 *
 * The draw-in and its reduced-motion answer are both CSS, in the styles file. That is deliberate: the
 * hero is pre-rendered and above the fold, so the stroke exists in the static HTML and starts drawing at
 * first paint, before hydration — an animation gated on a mounted effect would instead pop in whenever
 * JavaScript happened to arrive.
 */
function PencilUnderline() {
	return (
		<PencilStroke viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true" focusable="false">
			{/* The confident pass: one slightly-dipping sweep, thickest through the middle of the word. */}
			<PencilStrokeMain
				pathLength="1"
				vectorEffect="non-scaling-stroke"
				d="M4 12 C 46 5, 92 17, 140 9 S 186 6, 196 11"
			/>
			{/* The second, lighter pass a hand makes on the way back — shorter, and never a parallel copy. */}
			<PencilStrokeTail
				pathLength="1"
				vectorEffect="non-scaling-stroke"
				d="M22 17 C 70 12, 118 19, 172 14"
			/>
		</PencilStroke>
	);
}
